// DatabaseValidationSummary.jsx
import React, { useState } from 'react';
import '../../../styles/dataExports/DatabaseValidationSummary.css'; // Import your CSS styles here
import AssistantIcon from '@mui/icons-material/Assistant';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

const DatabaseValidationSummary = ({ results }) => {
  const [showRawJson, setShowRawJson] = useState(false);
  const [showSuccessful, setShowSuccessful] = useState(false);
  const [showFailed, setShowFailed] = useState(false);

  // If no results provided, create an empty template
  const validationResults = results || {
    success: [],
    failure: []
  };
  
  // Create error summary for failed tables
  const getErrorSummary = () => {
    if (validationResults.failure.length === 0) return [];
    
    // Group tables by the first 50 characters of the error message
    const errorGroups = {};
    validationResults.failure.forEach(table => {
      // Use the first 50 characters as the error group key
      const errorKey = table.error.substring(0, 50);
      
      if (!errorGroups[errorKey]) {
        errorGroups[errorKey] = {
          fullErrors: [table.error],
          tables: [],
          suggestion: table.suggestion
        };
      } else if (!errorGroups[errorKey].fullErrors.includes(table.error)) {
        // Track unique full error messages within this group
        errorGroups[errorKey].fullErrors.push(table.error);
        errorGroups[errorKey].suggestion = table.suggestion;
      }
      
      errorGroups[errorKey].tables.push(table.tableName);
    });
    
    // Convert to array format for rendering
    return Object.entries(errorGroups).map(([errorKey, data]) => {
      // If all errors in the group are identical, use the complete error
      // Otherwise use the common prefix and indicate there are variations
      let displayError = data.fullErrors.length === 1 
        ? data.fullErrors[0] 
        : `${errorKey}... (${data.fullErrors.length} occurrences)`;
      
      return {
        suggestion: data.suggestion,
        error: displayError,
        fullErrors: data.fullErrors,
        tables: data.tables,
        count: data.tables.length,
        variations: data.fullErrors.length
      };
    });
  };
  
  const errorSummary = getErrorSummary();
  
  return (
    <div className="db-validation-container">
      <div className="db-validation-header">
        {
            validationResults.failure.length > 0
            ?<h2 style={{color:"#f38ba8"}}> 
                <ReportProblemIcon sx={{color:"#f38ba8"}}/> 
                Oops, unsuccessfully saved settings
            </h2>
            : <h2 style={{color:"#a6e3a1"}}>           
                <svg className="icon" width="22" height="22" viewBox="0 0 16 16">
                    <path fill="currentColor" d="M6.5 12.5L3 9l1.5-1.5L6.5 9.5 12 4l1.5 1.5-7 7z"/>
                </svg>
                 Successfully saved settings
            </h2>
        }
      </div>
      
      <div className="db-validation-stats">
        <div className="stat-box">
          <span className="stat-number">{validationResults.success.length + validationResults.failure.length}</span>
          <span className="stat-label">Total Tables</span>
        </div>
        <div className="stat-box success">
          <span className="stat-number">{validationResults.success.length}</span>
          <span className="stat-label">Successful</span>
        </div>
        <div className="stat-box failure">
          <span className="stat-number">{validationResults.failure.length}</span>
          <span className="stat-label">Failed</span>
        </div>
      </div>
      
      {/* Error Summary Section (always visible) */}
      {validationResults.failure.length > 0 && (
        <div className="db-validation-section">
          <div className="section-header summary">
            <svg className="icon" width="16" height="16" viewBox="0 0 16 16">
              <path fill="currentColor" d="M8 16A8 8 0 1 1 8 0a8 8 0 0 1 0 16zm0-2A6 6 0 1 0 8 2a6 6 0 0 0 0 12zm-.8-8h1.5v4.5h-1.5V6zm0-3h1.5v1.5h-1.5V3z"/>
            </svg>
            <h3>Error Summary</h3>
          </div>
          
          <div className="section-content">
            <div className="error-summary-list">
              {errorSummary.map((group, index) => (
                <div key={index} className="error-summary-item">
                  <div className="error-summary-message">
                    <div style={{marginRight:"5px"}}>
                        <ReportProblemIcon fontSize="14px" sx={{color:"#f38ba8"}}/>
                    </div>
                    <div>{group.error}</div>
                  </div>
                  <div className="error-suggestion-message">
                    <div style={{marginRight:"5px"}}>
                        <AssistantIcon fontSize="14px" sx={{color:"#a6e3a1"}}/>
                    </div>
                    <div>{group.suggestion}</div>
                  </div>
                  {group.variations > 1 && (
                    <div className="error-variations">
                      <details>
                        <summary className="variations-summary">Show all {group.variations} error occurrences</summary>
                        <div className="variations-list">
                          {group.fullErrors.map((error, errIdx) => (
                            <div key={errIdx} className="variation-item">{error}</div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}
                  <div className="error-summary-tables">
                    <span className="error-count">{group.count} {group.count === 1 ? 'table' : 'tables'} affected: </span>
                    <span className="error-table-list">{group.tables.join(', ')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Successful Tables Section (foldable) */}
      <div className="db-validation-section">
        <div 
          className="section-header success clickable"
          onClick={() => setShowSuccessful(!showSuccessful)}
        >
          <svg className="icon" width="16" height="16" viewBox="0 0 16 16">
            <path fill="currentColor" d="M6.5 12.5L3 9l1.5-1.5L6.5 9.5 12 4l1.5 1.5-7 7z"/>
          </svg>
          <h3>Successful Tables ({validationResults.success.length})</h3>
          <svg className="toggle-icon" width="16" height="16" viewBox="0 0 16 16">
            <path fill="currentColor" d={showSuccessful ? "M2 5l6 6 6-6" : "M6 12l4-4-4-4"}/>
          </svg>
        </div>
        
        {showSuccessful && (
          <div className="section-content">
            {validationResults.success.length === 0 ? (
              <div className="empty-message">No successful tables to display</div>
            ) : (
              <div className="table-list">
                {validationResults.success.map((status, index) => (
                  <div key={index} className="table-item">
                    <div className="table-name">{status.tableName}</div>
                    <div className="table-status">
                      <span className="status-item">
                        Exists: <span className={status.exists ? "status-success" : "status-failure"}>
                          {status.exists ? "Yes" : "No"}
                        </span>
                      </span>
                      <span className="status-item">
                        Created: <span className={status.created ? "status-new" : "status-existing"}>
                          {status.created ? "Yes (new)" : "No (existing)"}
                        </span>
                      </span>
                      <span className="status-item">
                        Can Insert: <span className={status.canInsert ? "status-success" : "status-failure"}>
                          {status.canInsert ? "Yes" : "No"}
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Failed Tables Section (foldable) */}
      <div className="db-validation-section">
        <div 
          className="section-header failure clickable"
          onClick={() => setShowFailed(!showFailed)}
        >
          <svg className="icon" width="16" height="16" viewBox="0 0 16 16">
            <path fill="currentColor" d="M12 4l-8 8M4 4l8 8"/>
          </svg>
          <h3>Failed Tables ({validationResults.failure.length})</h3>
          <svg className="toggle-icon" width="16" height="16" viewBox="0 0 16 16">
            <path fill="currentColor" d={showFailed ? "M2 5l6 6 6-6" : "M6 12l4-4-4-4"}/>
          </svg>
        </div>
        
        {showFailed && (
          <div className="section-content">
            {validationResults.failure.length === 0 ? (
              <div className="empty-message">No failed tables to display</div>
            ) : (
              <div className="table-list">
                {validationResults.failure.map((status, index) => (
                  <div key={index} className="table-item error">
                    <div className="table-name">{status.tableName}</div>
                    <div className="table-status">
                      <span className="status-item">
                        Exists: <span className={status.exists ? "status-success" : "status-failure"}>
                          {status.exists ? "Yes" : "No"}
                        </span>
                      </span>
                      <span className="status-item">
                        Create If Non-Existent: <span className={status.createIfNonExist ? "status-success" : "status-neutral"}>
                          {status.createIfNonExist ? "Yes" : "No"}
                        </span>
                      </span>
                    </div>
                    <div className="error-message">
                        <div style={{marginRight:"5px"}}>
                            <ReportProblemIcon fontSize="14px" sx={{color:"#f38ba8"}}/>
                        </div>
                      <span>{status.error}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Raw JSON Toggle */}
      <div className="db-validation-footer">
        <button 
          onClick={() => setShowRawJson(!showRawJson)}
          className="toggle-button"
        >
          {showRawJson ? "Hide Raw JSON" : "Show Raw JSON"}
        </button>
      </div>
      
      {/* Raw JSON Display */}
      {showRawJson && (
        <div className="json-display">
          <pre>
            {JSON.stringify(
              {
                timestamp: new Date().toISOString(),
                summary: {
                  total: validationResults.success.length + validationResults.failure.length,
                  successful: validationResults.success.length,
                  failed: validationResults.failure.length
                },
                successfulTables: validationResults.success,
                failedTables: validationResults.failure
              },
              null,
              2
            )}
          </pre>
        </div>
      )}
    </div>
  );
};

export default DatabaseValidationSummary;